import { useMemo } from 'react'
import { 
  ComposedChart,
  Bar, 
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'
import { AlertCircle, Target, Download } from 'lucide-react'
import { prepareParetoChartData, getOperatorColors, formatPercent } from '../utils/chartHelpers'

/**
 * Custom tooltip for Pareto chart
 */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  
  const data = payload[0].payload
  
  return (
    <div className="bg-white border border-brand-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-brand-gray-800 mb-1">{data.salon}</p>
      <p className="text-sm text-brand-gray-600">
        <span className="font-medium">Plant:</span> {data.plantCode}
      </p>
      <p className="text-sm text-brand-gray-600">
        <span className="font-medium">Sprzedaż:</span> {data.sales.toLocaleString()} szt.
      </p>
      <p className="text-sm font-bold text-purple-600 mt-1">
        Skumulowany: {data.cumulative.toFixed(1)}%
      </p>
    </div>
  )
}

/**
 * Pareto Statistics Card
 */
const ParetoStatCard = ({ title, salonsCount, percentage, variant = 'default' }) => {
  const variants = {
    default: 'border-brand-gray-200',
    '80': 'border-red-300 bg-red-50',
    '70': 'border-amber-300 bg-amber-50'
  }

  return (
    <div className={`rounded-lg border p-4 ${variants[variant]}`}>
      <div className="flex items-center mb-2">
        <Target className={`h-4 w-4 mr-2 ${variant === '80' ? 'text-red-600' : variant === '70' ? 'text-amber-600' : 'text-brand-gray-500'}`} />
        <span className="text-sm font-semibold text-brand-gray-700">{title}</span>
      </div>
      <p className="text-sm text-brand-gray-600">
        <span className="font-bold text-lg text-brand-gray-900">{salonsCount}</span> salonów
        <span className="text-brand-gray-400 mx-1">•</span>
        <span className="font-medium">{formatPercent(percentage)}</span> wszystkich
      </p>
    </div>
  )
}

/**
 * Pareto Analysis sub-report component
 */
const ParetoAnalysis = ({ 
  data, 
  paretoStats,
  operator,
  onExportExcel
}) => {
  const chartData = useMemo(() => {
    return prepareParetoChartData(data?.salon_data, 100)
  }, [data?.salon_data])

  const colors = getOperatorColors(operator)
  const pareto80 = paretoStats?.['80_20']
  const pareto70 = paretoStats?.['70_30']

  if (!chartData.length) {
    return (
      <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mb-3" />
          <h3 className="text-lg font-semibold text-brand-gray-800 mb-1">
            Brak danych
          </h3>
          <p className="text-sm text-brand-gray-500">
            Brak danych do wyświetlenia analizy Pareto
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Pareto Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ParetoStatCard
          title="Reguła 80/20"
          salonsCount={pareto80?.top_salons_count || 0}
          percentage={pareto80?.salon_percentage || 0}
          variant="80"
        />
        <ParetoStatCard
          title="Reguła 70/30"
          salonsCount={pareto70?.top_salons_count || 0}
          percentage={pareto70?.salon_percentage || 0}
          variant="70"
        />
        <div className="rounded-lg border border-brand-gray-200 p-4 flex items-center justify-center">
          {onExportExcel && (
            <button
              onClick={onExportExcel}
              className="flex items-center px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-blue-dark transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Eksport do Excel
            </button>
          )}
        </div>
      </div>

      {/* Pareto Chart */}
      <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-brand-gray-800">
            Analiza Pareto - rozkład sprzedaży
          </h3>
          <p className="text-sm text-brand-gray-500 mt-1">
            Analiza {chartData.length} salonów - słupki pokazują sprzedaż, linia skumulowany procent
          </p>
        </div>

        <div className="h-[450px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart 
              data={chartData}
              margin={{ top: 20, right: 60, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="index" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                label={{ 
                  value: 'Ranking salonu', 
                  position: 'insideBottom', 
                  offset: -10,
                  style: { fontSize: 12, fill: '#6b7280' }
                }}
              />
              <YAxis 
                yAxisId="left"
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                label={{ 
                  value: 'Skumulowany %', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fontSize: 12, fill: '#6b7280' }
                }}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={(value) => value.toLocaleString()}
                label={{ 
                  value: 'Sprzedaż', 
                  angle: 90, 
                  position: 'insideRight',
                  style: { fontSize: 12, fill: '#6b7280' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                yAxisId="right" 
                dataKey="sales" 
                fill={colors.primary}
                opacity={0.7}
                radius={[2, 2, 0, 0]}
              />
              <Line 
                yAxisId="left" 
                type="monotone" 
                dataKey="cumulative" 
                stroke="#9333ea"
                strokeWidth={3}
                dot={{ r: 2 }}
              />
              <ReferenceLine 
                yAxisId="left" 
                y={80} 
                stroke="#ef4444" 
                strokeDasharray="5 5" 
                label={{ value: '80%', position: 'right', fill: '#ef4444', fontSize: 12 }}
              />
              <ReferenceLine 
                yAxisId="left" 
                y={70} 
                stroke="#f59e0b" 
                strokeDasharray="5 5" 
                label={{ value: '70%', position: 'right', fill: '#f59e0b', fontSize: 12 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Interpretation */}
        <div className="mt-4 p-4 bg-brand-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-brand-gray-700 mb-2">Interpretacja</h4>
          {pareto80 && pareto80.salon_percentage <= 20 ? (
            <p className="text-sm text-green-700">
              <span className="font-bold">Bardzo silna koncentracja</span> - tylko {formatPercent(pareto80.salon_percentage)} salonów 
              generuje 80% sprzedaży. Klasyczny rozkład Pareto!
            </p>
          ) : pareto80 && pareto80.salon_percentage <= 30 ? (
            <p className="text-sm text-green-600">
              <span className="font-bold">Silna koncentracja</span> - {formatPercent(pareto80.salon_percentage)} salonów 
              generuje 80% sprzedaży. Dobra koncentracja.
            </p>
          ) : pareto80 && pareto80.salon_percentage <= 50 ? (
            <p className="text-sm text-amber-600">
              <span className="font-bold">Średnia koncentracja</span> - {formatPercent(pareto80.salon_percentage)} salonów 
              generuje 80% sprzedaży.
            </p>
          ) : pareto80 ? (
            <p className="text-sm text-red-600">
              <span className="font-bold">Słaba koncentracja</span> - aż {formatPercent(pareto80.salon_percentage)} salonów 
              potrzeba dla 80% sprzedaży.
            </p>
          ) : (
            <p className="text-sm text-brand-gray-500">Brak wystarczających danych do analizy.</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default ParetoAnalysis
